import React, { useState, useMemo } from "react";
import { Dialog } from "../../../../ui/components/Dialog";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import { Tabs } from "../../../../ui/components/Tabs";
import {
  FeatherX,
  FeatherDownload,
  FeatherCheck,
  FeatherAlertTriangle,
  FeatherXCircle,
} from "@subframe/core";
import { formatDate } from "../../../../shared/utils/formatters";

interface Transaction {
  date: string;
  description: string;
  amount: number;
  debit_or_credit?: string;
  payee?: string;
  balance?: number;
}

interface TransactionsData {
  duplicate: Transaction[];
  processed: Transaction[];
  not_imported: Transaction[];
}

interface TransactionReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionsJson?: string;
  documentName?: string;
}

export const TransactionReportModal: React.FC<TransactionReportModalProps> = ({
  open,
  onOpenChange,
  transactionsJson,
  documentName,
}) => {
  const [activeTab, setActiveTab] = useState<
    "processed" | "duplicate" | "not_imported"
  >("processed");

  const transactionsData: TransactionsData = useMemo(() => {
    if (!transactionsJson) {
      return { duplicate: [], processed: [], not_imported: [] };
    }

    try {
      const parsed = JSON.parse(transactionsJson);
      return {
        duplicate: parsed.duplicate || [],
        processed: parsed.processed || [],
        not_imported: parsed.not_imported || [],
      };
    } catch (error) {
      console.error("Failed to parse transactions JSON:", error);
      return { duplicate: [], processed: [], not_imported: [] };
    }
  }, [transactionsJson]);

  const getActiveTransactions = () => {
    switch (activeTab) {
      case "processed":
        return transactionsData.processed;
      case "duplicate":
        return transactionsData.duplicate;
      case "not_imported":
        return transactionsData.not_imported;
      default:
        return [];
    }
  };

  const handleDownload = () => {
    const activeTransactions = getActiveTransactions();
    if (activeTransactions.length === 0) return;

    // Convert to CSV
    const headers = [
      "Date",
      "Description",
      "Amount",
      "Type",
      "Payee",
      "Balance",
    ];
    const rows = activeTransactions.map((tx) => [
      tx.date || "",
      tx.description || "",
      tx.amount?.toString() || "",
      tx.debit_or_credit || "",
      tx.payee || "",
      tx.balance?.toString() || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${documentName || "transactions"}_${activeTab}_${
        new Date().toISOString().split("T")[0]
      }.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderTransactionsTable = (transactions: Transaction[]) => {
    if (transactions.length === 0) {
      return (
        <div className="flex w-full items-center justify-center py-12">
          <span className="text-body font-body text-neutral-500">
            No transactions found
          </span>
        </div>
      );
    }

    return (
      <Table
        className="w-full"
        style={{ tableLayout: "fixed" }}
        header={
          <Table.HeaderRow>
            <Table.HeaderCell className="w-[120px]">Date</Table.HeaderCell>
            <Table.HeaderCell className="w-[340px]">
              Description
            </Table.HeaderCell>
            <Table.HeaderCell className="w-[130px]">Amount</Table.HeaderCell>
            <Table.HeaderCell className="w-[120px]">Type</Table.HeaderCell>
            {/* {transactions.some((tx) => tx.payee) && (
              <Table.HeaderCell className="w-[220px]">Payee</Table.HeaderCell>
            )} */}
            {/* {transactions.some((tx) => tx.balance !== undefined) && (
              <Table.HeaderCell className="w-[130px]">Balance</Table.HeaderCell>
            )} */}
          </Table.HeaderRow>
        }
      >
        {transactions.map((transaction, index) => (
          <Table.Row key={index}>
            <Table.Cell className="w-[120px]">
              <span className="block w-full truncate text-caption font-caption text-default-font">
                {transaction.date ? formatDate(transaction.date, "short") : "-"}
              </span>
            </Table.Cell>
            <Table.Cell className="w-[340px]">
              <span
                className="block w-full truncate text-caption font-caption text-default-font"
                title={transaction.description || "-"}
              >
                {transaction.description || "-"}
              </span>
            </Table.Cell>
            <Table.Cell className="w-[130px]">
              <span className="block w-full truncate text-caption font-caption text-default-font">
                {transaction.amount !== undefined
                  ? transaction.amount.toFixed(2)
                  : "-"}
              </span>
            </Table.Cell>
            <Table.Cell className="w-[120px]">
              {transaction.debit_or_credit ? (
                <Badge
                  variant={
                    transaction.debit_or_credit === "debit"
                      ? "error"
                      : "success"
                  }
                  className="h-5 px-1.5"
                >
                  <span className="text-[11px] leading-4">
                    {transaction.debit_or_credit}
                  </span>
                </Badge>
              ) : (
                <span className="block w-full truncate text-caption font-caption text-neutral-500">
                  -
                </span>
              )}
            </Table.Cell>
            {/* {transactions.some((tx) => tx.payee) && (
              <Table.Cell className="w-[220px]">
                <span
                  className="block w-full truncate text-caption font-caption text-default-font"
                  title={transaction.payee || "-"}
                >
                  {transaction.payee || "-"}
                </span>
              </Table.Cell>
            )} */}
            {/* {transactions.some((tx) => tx.balance !== undefined) && (
              <Table.Cell className="w-[130px]">
                <span className="block w-full truncate text-caption font-caption text-default-font">
                  {transaction.balance !== undefined
                    ? transaction.balance.toFixed(2)
                    : "-"}
                </span>
              </Table.Cell>
            )} */}
          </Table.Row>
        ))}
      </Table>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="flex w-full max-w-6xl flex-col gap-4 p-6">
        {/* Header */}
        <div className="flex w-full items-center justify-between">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Transaction Report
            </span>
            {documentName && (
              <span
                className="block w-full max-w-[40rem] truncate text-body font-body text-subtext-color"
                title={documentName}
              >
                {documentName}
              </span>
            )}
          </div>
          <Button
            variant="neutral-tertiary"
            size="small"
            icon={<FeatherX />}
            onClick={() => onOpenChange(false)}
          />
        </div>

        {/* Tabs */}
        <Tabs>
          <Tabs.Item
            active={activeTab === "processed"}
            onClick={() => setActiveTab("processed")}
            icon={<FeatherCheck />}
          >
            Processed ({transactionsData.processed.length})
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "duplicate"}
            onClick={() => setActiveTab("duplicate")}
            icon={<FeatherAlertTriangle />}
          >
            Duplicates ({transactionsData.duplicate.length})
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "not_imported"}
            onClick={() => setActiveTab("not_imported")}
            icon={<FeatherXCircle />}
          >
            Not Imported ({transactionsData.not_imported.length})
          </Tabs.Item>
        </Tabs>

        {/* Download Button */}
        <div className="flex w-full items-center justify-end">
          <Button
            variant="brand-primary"
            size="small"
            icon={<FeatherDownload />}
            onClick={handleDownload}
            disabled={getActiveTransactions().length === 0}
          >
            Download CSV
          </Button>
        </div>

        {/* Transactions Table */}
        <div className="flex w-full flex-col overflow-auto max-h-[60vh]">
          {renderTransactionsTable(getActiveTransactions())}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
